"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'

const WAGON_TYPE_OPTIONS = [
  { value: 'flat', label: 'Flat' },
  { value: 'tank', label: 'Tank' },
  { value: 'hopper', label: 'Hopper' },
  { value: 'box', label: 'Box' },
  { value: 'intermodal', label: 'Intermodal' },
  { value: 'refrigerated', label: 'Refrigerated' },
  { value: 'other', label: 'Other' },
]

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'in_use', label: 'In Use' },
]

export default function CreateWagonPage() {
  const t = useT()
  const router = useRouter()
  const { organizationId, tenantId } = useOrganizationScopeDetail()

  const fields = React.useMemo<CrudField[]>(() => [
    { id: 'evn', label: t('rolling_stock.wagons.fields.evn', 'EVN (12 digits)'), type: 'text', required: true, placeholder: '310000000000', maxLength: 12 },
    { id: 'name', label: t('rolling_stock.wagons.fields.name', 'Name'), type: 'text', required: true, placeholder: 'Sgns 60ft' },
    { id: 'series', label: t('rolling_stock.wagons.fields.series', 'Series'), type: 'text', required: true, placeholder: 'Sgns' },
    { id: 'wagonType', label: t('rolling_stock.wagons.fields.wagonType', 'Wagon Type'), type: 'select', required: true, options: WAGON_TYPE_OPTIONS },
    { id: 'status', label: t('rolling_stock.wagons.fields.status', 'Status'), type: 'select', required: true, options: STATUS_OPTIONS },
    { id: 'weightTonnes', label: t('rolling_stock.wagons.fields.weightTonnes', 'Tare Weight (tonnes)'), type: 'number', required: true, min: 0 },
    { id: 'maxLoadTonnes', label: t('rolling_stock.wagons.fields.maxLoadTonnes', 'Max Load (tonnes)'), type: 'number', min: 0 },
    { id: 'lengthMetres', label: t('rolling_stock.wagons.fields.lengthMetres', 'Length over buffers (m)'), type: 'number', min: 0 },
    { id: 'ecmName', label: t('rolling_stock.wagons.fields.ecmName', 'ECM Name'), type: 'text', required: true },
    { id: 'ecmCertificateId', label: t('rolling_stock.wagons.fields.ecmCertificateId', 'ECM Certificate ID'), type: 'text' },
    { id: 'ecmValidUntil', label: t('rolling_stock.wagons.fields.ecmValidUntil', 'ECM Valid Until'), type: 'date' },
    { id: 'notes', label: t('rolling_stock.wagons.fields.notes', 'Notes'), type: 'textarea' },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'identification',
      title: t('rolling_stock.wagons.groups.identification', 'Identification'),
      fields: ['evn', 'name', 'series', 'wagonType', 'status'],
    },
    {
      id: 'dimensions',
      title: t('rolling_stock.wagons.groups.dimensions', 'Dimensions & Capacity'),
      fields: ['weightTonnes', 'maxLoadTonnes', 'lengthMetres'],
    },
    {
      id: 'ecm',
      title: t('rolling_stock.wagons.groups.ecm', 'Entity in Charge of Maintenance'),
      fields: ['ecmName', 'ecmCertificateId', 'ecmValidUntil'],
    },
    {
      id: 'other',
      title: t('rolling_stock.wagons.groups.other', 'Other'),
      fields: ['notes'],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('rolling_stock.wagons.create.title', 'Add Wagon')}
          backHref="/backend/rolling-stock/wagons"
          fields={fields}
          groups={groups}
          initialValues={{ status: 'available' }}
          submitLabel={t('rolling_stock.wagons.create.submit', 'Create Wagon')}
          cancelHref="/backend/rolling-stock/wagons"
          onSubmit={async (values) => {
            const payload = {
              ...values,
              organizationId,
              tenantId,
            }
            const { result: created } = await createCrud<{ id?: string }>('rolling-stock/wagons', payload)
            flash(t('rolling_stock.wagons.flash.created', 'Wagon created'), 'success')
            router.push(`/backend/rolling-stock/wagons/${created?.id}`)
          }}
        />
      </PageBody>
    </Page>
  )
}
